import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { FullPageSpinner } from "./components/FullPageSpinner";
import { RequirePlatformStaff } from "./components/RequirePlatformStaff";
import { RequirePortal } from "./components/RequirePortal";
import { useAuthStore } from "./lib/auth-store";
import { landingPath } from "./lib/session";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { NoAccessPage } from "./pages/NoAccessPage";
import { ModuleVariantsPage } from "./pages/ModuleVariantsPage";
import { ButtonVariantsPage } from "./pages/ButtonVariantsPage";
import { BrandVariantsPage } from "./pages/BrandVariantsPage";

/**
 * One application, four shells.
 *
 * Each portal is a lazily-loaded route tree, so a parent opening the app on a
 * 3G connection downloads the parent bundle and nothing else — never the admin
 * tables or the report-card builder. That code splitting is what makes "one
 * app instead of four" cost nothing at runtime.
 *
 * These boundaries are for navigation and payload size only. Every one of them
 * is enforced again server-side by CASL abilities and Row-Level Security; a
 * user who edits the URL reaches a screen that returns 403s.
 */
const AdminPortal = lazy(() => import("./portals/admin/AdminPortal"));
const TeachPortal = lazy(() => import("./portals/teach/TeachPortal"));
const ParentPortal = lazy(() => import("./portals/parent/ParentPortal"));
const StudentPortal = lazy(() => import("./portals/student/StudentPortal"));
const PlatformPortal = lazy(() => import("./portals/platform/PlatformPortal"));

/**
 * The public demo — an invented school held entirely in the browser, so the
 * marketing page has something to show without a tenant, a database or a
 * login. Lazy for the same reason the portals are: nobody signing in to their
 * own school should download it.
 */
const DemoApp = lazy(() => import("./demo/DemoApp"));

/**
 * The rest of the public site. Lazy for the same reason the demo is: somebody
 * signing in to their own school every morning should not carry the pricing
 * estimator and the onboarding sequence in their first-load bundle. The
 * landing page itself stays eager because it is what "/" renders.
 */
const PricingPage = lazy(() =>
  import("./pages/PricingPage").then((m) => ({ default: m.PricingPage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage })),
);
const AboutPage = lazy(() => import("./pages/AboutPage").then((m) => ({ default: m.AboutPage })));

export function App() {
  const { user, initialising } = useAuthStore();

  if (initialising) return <FullPageSpinner label="Signing you in" />;

  return (
    <Suspense fallback={<FullPageSpinner label="Loading" />}>
      <Routes>
        <Route path="/" element={user ? <Navigate to={landingPath(user)} replace /> : <LandingPage />} />
        <Route path="/login" element={user ? <Navigate to={landingPath(user)} replace /> : <LoginPage />} />
        <Route path="/no-access" element={<NoAccessPage />} />

        {/* Deliberately reachable whether or not anyone is signed in, and
            never redirected away from: these are marketing surfaces, not
            portals, and they read no session. A signed-in director following a
            link to the pricing page should land on the pricing page, not be
            bounced into their own dashboard. */}
        <Route path="/demo/*" element={<DemoApp />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/module-variants" element={<ModuleVariantsPage />} />
        <Route path="/button-variants" element={<ButtonVariantsPage />} />
        <Route path="/brand-variants" element={<BrandVariantsPage />} />

        <Route
          path="/admin/*"
          element={
            <RequirePortal portal="admin">
              <AdminPortal />
            </RequirePortal>
          }
        />
        <Route
          path="/teach/*"
          element={
            <RequirePortal portal="teach">
              <TeachPortal />
            </RequirePortal>
          }
        />
        <Route
          path="/parent/*"
          element={
            <RequirePortal portal="parent">
              <ParentPortal />
            </RequirePortal>
          }
        />
        <Route
          path="/me/*"
          element={
            <RequirePortal portal="student">
              <StudentPortal />
            </RequirePortal>
          }
        />
        <Route
          path="/platform/*"
          element={
            <RequirePlatformStaff>
              <PlatformPortal />
            </RequirePlatformStaff>
          }
        />

        <Route path="*" element={<Navigate to={landingPath(user)} replace />} />
      </Routes>
    </Suspense>
  );
}
